import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
  createLinkCode,
  decideLink,
  DeskLink,
  DeskLinksAnswer,
  endDeskLink,
  getDeskLinks,
  NewLinkCode,
  revokeLinkCode,
} from '../../../../api/building-links-client';
import { listUnits } from '../../../../api/hierarchy-client';
import { StaysApiError } from '../../../../api/stays-client';
import { formatDate } from '../../../../function-utils/locale';
import { palette, radius, spacing, typography } from '../../../../style-utils/theme';
import toast from '../../../../utils/toast';
import { AppButton } from '../../../button/app-button';
import { Field, Label, Select } from '../../../form-kit';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { StatusPill } from '../../../status-pill/status-pill';
import { ERROR_COPY } from '../dashboard-stays-components/stays-labels';
import { UnitsSection, UnitsSectionHead, UnitsSectionNote, UnitsSectionTitle } from './dashboard-buildings.styled';

/**
 * SHORT-STAY HOSTS — the building's side of the link (Independent Stays plan IS7).
 *
 * The manager creates a code (shown once) and gives it to a host in another account.
 * The host names their apartment; the manager matches it to a unit here and approves.
 * The host never sees this building's units or staff. Ending a link takes its future
 * arrivals off the desk at once; arrivals already logged stay in this building's log.
 *
 * The caller mounts it only for a real building id, building:link_hosts and the switch.
 */

const CodeBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.sm} ${spacing.md};
  border-radius: ${radius.md};
  background: ${palette.irisTintSoft};
`;

const CodeText = styled.code`
  font-family: ${typography.mono};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: 0.04em;
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

const Items = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  margin: ${spacing.sm} 0 0;
  padding: 0;
`;

const Item = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
`;

const ItemHead = styled.div`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const ItemSub = styled.div`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${spacing.sm};
`;

const wordsOf = (e: unknown, fallback: string): string => {
  if (e instanceof StaysApiError && e.code && (ERROR_COPY as Record<string, string>)[e.code]) {
    return (ERROR_COPY as Record<string, string>)[e.code];
  }

  return e instanceof Error && e.message ? e.message : fallback;
};

const dayOf = (at: string): string => formatDate(at, { day: 'numeric', month: 'short', year: 'numeric' });

export interface BuildingLinkSectionProps {
  buildingId: number;
  buildingName: string;
}

export const BuildingLinkSection: React.FC<BuildingLinkSectionProps> = ({ buildingId, buildingName }) => {
  const [data, setData] = useState<DeskLinksAnswer | null>(null);
  const [failed, setFailed] = useState(false);
  const [units, setUnits] = useState<{ id: number; label: string }[]>([]);
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [fresh, setFresh] = useState<NewLinkCode | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [ending, setEnding] = useState<DeskLink | null>(null);

  const read = useCallback(() => {
    getDeskLinks(buildingId)
      .then(d => {
        setData(d);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  }, [buildingId]);

  useEffect(() => {
    read();
    listUnits(buildingId)
      .then(list => setUnits(list.map(u => ({ id: Number(u.id), label: String(u.unit_number) }))))
      .catch(() => setUnits([]));
  }, [buildingId, read]);

  const act = async (key: string, work: () => Promise<unknown>, fallback: string) => {
    if (busy) return;
    setBusy(key);
    try {
      await work();
      read();
    } catch (e) {
      toast.error(wordsOf(e, fallback));
      read();
    } finally {
      setBusy(null);
    }
  };

  const copy = async () => {
    if (!fresh) return;
    try {
      await navigator.clipboard.writeText(fresh.code);
      toast.success('Code copied.');
    } catch {
      toast.error('Could not copy. Select the code and copy it by hand.');
    }
  };

  const requests = (data?.links || []).filter(l => l.status === 'requested');
  const live = (data?.links || []).filter(l => l.status === 'approved');
  const taken = new Set(live.map(l => l.desk_unit_id));

  return (
    <UnitsSection aria-label={`Short-stay hosts in ${buildingName}`} data-testid="building-link-section">
      <UnitsSectionHead>
        <UnitsSectionTitle>Short-stay hosts</UnitsSectionTitle>
      </UnitsSectionHead>
      <UnitsSectionNote>
        A host with an apartment here who uses Lobbero Stays can link it to your front desk. Their guests then appear in
        Expected today, once the guest agrees to it. Neither of you sees the other&apos;s staff or settings.
      </UnitsSectionNote>

      {failed ? <UnitsSectionNote role="alert">Could not load the short-stay hosts. Try again.</UnitsSectionNote> : null}

      {fresh ? (
        <CodeBox data-testid="link-code-shown">
          <CodeText>{fresh.code}</CodeText>
          <AppButton variant="secondary" onClick={copy}>
            Copy
          </AppButton>
          <ItemSub>
            Shown once. Works one time, until <span data-at={fresh.expires_at}>{dayOf(fresh.expires_at)}</span>.
          </ItemSub>
        </CodeBox>
      ) : null}

      <Actions>
        <AppButton
          variant="secondary"
          disabled={!!busy}
          onClick={() =>
            act(
              'code',
              async () => {
                setFresh(await createLinkCode(buildingId));
              },
              'Could not create a link code.'
            )
          }
        >
          Create a link code
        </AppButton>
      </Actions>

      {(data?.codes || []).length ? (
        <Items aria-label="Unused link codes">
          {(data?.codes || []).map(c => (
            <Item key={c.id} data-link-code={c.id}>
              <ItemSub>
                A code is waiting to be used, until <span data-at={c.expires_at}>{dayOf(c.expires_at)}</span>.
              </ItemSub>
              <Actions>
                <AppButton
                  variant="ghost"
                  disabled={!!busy}
                  onClick={() => act(`revoke:${c.id}`, () => revokeLinkCode(c.id), 'Could not cancel the code.')}
                >
                  Cancel this code
                </AppButton>
              </Actions>
            </Item>
          ))}
        </Items>
      ) : null}

      {requests.length ? (
        <Items aria-label="Requests waiting for you">
          {requests.map(l => (
            <Item key={l.id} data-link-request={l.id}>
              <ItemHead>
                {`${l.requester_name || 'A host'} (${l.host_kind} host) asks to link apartment "${l.apartment_label}"`}
              </ItemHead>
              <Actions>
                <Field>
                  <Label htmlFor={`link-unit-${l.id}`}>Which unit is this?</Label>
                  <Select
                    id={`link-unit-${l.id}`}
                    value={picked[l.id] || ''}
                    onChange={e => setPicked(p => ({ ...p, [l.id]: e.target.value }))}
                  >
                    <option value="">Choose a unit</option>
                    {units.map(u => (
                      <option key={u.id} value={u.id} disabled={taken.has(u.id)}>
                        {taken.has(u.id) ? `${u.label} (already linked)` : u.label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <AppButton
                  disabled={!!busy || !picked[l.id]}
                  onClick={() =>
                    act(
                      `approve:${l.id}`,
                      () => decideLink(l.id, { approve: true, desk_unit_id: Number(picked[l.id]) }),
                      'Could not approve the link.'
                    )
                  }
                >
                  Approve
                </AppButton>
                <AppButton
                  variant="ghost"
                  disabled={!!busy}
                  onClick={() => act(`refuse:${l.id}`, () => decideLink(l.id, { approve: false }), 'Could not refuse the link.')}
                >
                  Refuse
                </AppButton>
              </Actions>
            </Item>
          ))}
        </Items>
      ) : null}

      {live.length ? (
        <Items aria-label="Linked apartments">
          {live.map(l => (
            <Item key={l.id} data-link-live={l.id}>
              <ItemHead>
                {`Unit ${l.unit_label || '—'} · ${l.requester_name || 'A host'} (${l.host_kind} host)`}{' '}
                <StatusPill tone="success" dot>
                  Linked
                </StatusPill>
              </ItemHead>
              <Actions>
                <AppButton variant="ghost" disabled={!!busy} onClick={() => setEnding(l)}>
                  End link
                </AppButton>
              </Actions>
            </Item>
          ))}
        </Items>
      ) : null}

      <ConfirmDialog
        open={!!ending}
        title="End this link?"
        message="Its guests stop appearing at your desk at once. Arrivals already in your visitor log stay there."
        primaryLabel="End link"
        primaryVariant="destructive"
        dismissLabel="Keep the link"
        onDismiss={() => setEnding(null)}
        onPrimary={() => {
          const l = ending;
          setEnding(null);
          if (l) void act(`end:${l.id}`, () => endDeskLink(l.id), 'Could not end the link.');
        }}
      />
    </UnitsSection>
  );
};
